<template>
  <div class="school-management">
    <!-- Action Bar -->
    <div class="action-bar">
      <div class="action-bar-left">
        <h2 class="section-title">
          <i class="fas fa-school"></i>
          Gestionar Página de Escuela
        </h2>
      </div>
      <div v-if="schoolContent?.updatedAt" class="action-bar-right">
        <div class="update-info">
          <i class="fas fa-clock"></i>
          <span class="last-update">
            {{ formatDate(schoolContent.updatedAt) }}
          </span>
          <span v-if="schoolContent.updatedBy" class="updated-by">
            por {{ schoolContent.updatedBy }}
          </span>
        </div>
      </div>
    </div>

    <!-- School Form -->
    <div class="school-form-section">
      <form @submit.prevent="handleUpdateSchool" class="school-form">
        <div class="form-group">
          <label for="school-eyebrow" class="form-label">Texto superior del hero</label>
          <input
            id="school-eyebrow"
            v-model="schoolForm.heroEyebrow"
            type="text"
            class="form-input"
            placeholder="Ej: Formación Musical"
            :disabled="updatingSchool"
            required
          />
        </div>

        <div class="form-group">
          <label for="school-title" class="form-label">Título principal</label>
          <input
            id="school-title"
            v-model="schoolForm.heroTitle"
            type="text"
            class="form-input"
            placeholder="Ej: Escola de Música"
            :disabled="updatingSchool"
            required
          />
        </div>

        <div class="form-group">
          <label for="school-subtitle" class="form-label">Subtítulo</label>
          <textarea
            id="school-subtitle"
            v-model="schoolForm.heroSubtitle"
            class="form-textarea"
            placeholder="Descripción breve de la escuela"
            rows="4"
            :disabled="updatingSchool"
            required
          ></textarea>
        </div>

        <div class="form-actions">
          <button
            type="submit"
            class="update-button"
            :disabled="updatingSchool || !schoolForm.heroEyebrow.trim() || !schoolForm.heroTitle.trim() || !schoolForm.heroSubtitle.trim()"
          >
            <span v-if="updatingSchool" class="loading-spinner">
              <i class="fas fa-spinner fa-spin"></i>
            </span>
            <span v-else>
              <i class="fas fa-save"></i>
              Actualizar Página de Escuela
            </span>
          </button>
        </div>
      </form>
    </div>

    <!-- Preview Section -->
    <div class="preview-section">
      <h3 class="preview-title">
        <i class="fas fa-eye"></i>
        Vista Previa
      </h3>
      <div class="preview-card">
        <div class="preview-hero">
          <div class="preview-eyebrow">{{ schoolForm.heroEyebrow || 'Texto superior' }}</div>
          <h1 class="preview-title-text">{{ schoolForm.heroTitle || 'Título principal' }}</h1>
          <p class="preview-subtitle">{{ schoolForm.heroSubtitle || 'Descripción de la escuela...' }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useSchool } from '@/composables/useSchool';

const schoolComposable = useSchool();
const { updateSchool, fetchSchool } = schoolComposable;
const schoolContent = schoolComposable.schoolContent;

const updatingSchool = ref(false);

const schoolForm = ref({
  heroEyebrow: '',
  heroTitle: '',
  heroSubtitle: '',
});

// Load school form data
const loadSchoolForm = () => {
  if (schoolContent.value) {
    schoolForm.value = {
      heroEyebrow: schoolContent.value.heroEyebrow,
      heroTitle: schoolContent.value.heroTitle,
      heroSubtitle: schoolContent.value.heroSubtitle,
    };
  }
};

// Watch for changes in school content
watch(schoolContent, () => {
  loadSchoolForm();
});

const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const handleUpdateSchool = async () => {
  if (!schoolForm.value.heroEyebrow.trim() || !schoolForm.value.heroTitle.trim() || !schoolForm.value.heroSubtitle.trim())
    return;

  updatingSchool.value = true;
  await updateSchool(schoolForm.value.heroEyebrow, schoolForm.value.heroTitle, schoolForm.value.heroSubtitle);
  updatingSchool.value = false;
};

onMounted(async () => {
  await fetchSchool();
  loadSchoolForm();
});
</script>

<style scoped>
.school-management {
  width: 100%;
}

/* Action Bar */
.action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-6);
  padding: var(--spacing-5);
  background: white;
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-md);
  gap: var(--spacing-4);
}

.action-bar-left {
  display: flex;
  align-items: center;
}

.action-bar-right {
  display: flex;
  align-items: center;
}

.section-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-900);
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
}

.section-title i {
  color: var(--color-secondary-600);
}

.update-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-4);
  background: var(--color-secondary-50);
  border: 1px solid var(--color-secondary-200);
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-sm);
  color: var(--color-secondary-700);
}

.update-info i {
  color: var(--color-secondary-600);
}

.last-update {
  font-weight: var(--font-weight-medium);
}

.updated-by {
  color: var(--color-secondary-600);
  font-weight: var(--font-weight-normal);
}

.school-form-section {
  background: white;
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  margin-bottom: var(--spacing-6);
  overflow: hidden;
}

.school-form {
  padding: var(--spacing-6);
}

.form-group {
  margin-bottom: var(--spacing-4);
}

.form-label {
  display: block;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-neutral-700);
  margin-bottom: var(--spacing-2);
}

.form-input,
.form-textarea {
  width: 100%;
  padding: var(--spacing-3) var(--spacing-4);
  border: 1px solid var(--color-neutral-300);
  border-radius: var(--border-radius-lg);
  font-size: var(--font-size-base);
  transition: var(--transition-all);
  background: white;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--color-secondary-500);
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.form-input:disabled,
.form-textarea:disabled {
  background: var(--color-neutral-100);
  cursor: not-allowed;
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-3);
  margin-top: var(--spacing-6);
}

.update-button {
  background: var(--color-secondary-600);
  color: white;
  border: 1px solid var(--color-secondary-600);
  padding: var(--spacing-3) var(--spacing-6);
  border-radius: var(--border-radius-lg);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-all);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  font-size: var(--font-size-base);
}

.update-button:hover:not(:disabled) {
  background: var(--color-secondary-700);
  border-color: var(--color-secondary-700);
  transform: translateY(-1px);
}

.update-button:disabled {
  background: var(--color-neutral-400);
  border-color: var(--color-neutral-400);
  cursor: not-allowed;
  transform: none;
}

.preview-section {
  margin-top: var(--spacing-6);
}

.preview-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-900);
  margin: 0 0 var(--spacing-4) 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

.preview-card {
  background: white;
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}

.preview-hero {
  background: linear-gradient(135deg, var(--color-secondary-50), var(--color-secondary-100));
  padding: var(--spacing-8) var(--spacing-6);
  text-align: center;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.preview-eyebrow {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-secondary-600);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: var(--spacing-2);
}

.preview-title-text {
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-secondary-900);
  margin: 0 0 var(--spacing-4) 0;
  line-height: 1.2;
}

.preview-subtitle {
  font-size: var(--font-size-lg);
  color: var(--color-secondary-700);
  margin: 0;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.5;
}

/* Loading spinner */
.loading-spinner {
  font-size: 1rem;
  color: white;
}

/* Responsive Design */
@media (max-width: 768px) {
  .action-bar {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-4);
  }

  .action-bar-left,
  .action-bar-right {
    width: 100%;
  }

  .update-info {
    justify-content: center;
  }

  .school-form {
    padding: var(--spacing-4);
  }

  .form-actions {
    flex-direction: column;
  }

  .update-button {
    width: 100%;
    justify-content: center;
  }

  .preview-hero {
    padding: var(--spacing-6) var(--spacing-4);
  }

  .preview-title-text {
    font-size: var(--font-size-2xl);
  }
}

@media (max-width: 640px) {
  .preview-eyebrow {
    font-size: 0.75rem;
  }

  .preview-title-text {
    font-size: var(--font-size-xl);
  }

  .preview-subtitle {
    font-size: var(--font-size-base);
  }
}
</style>


